import { GridOptions } from 'ag-grid-community';
import { memoize } from 'lodash';
import React, {
  ComponentType,
  FC,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { setDisplayName, wrapDisplayName } from 'recompose';
import { useOnGridReady } from './use-on-grid-ready';
import { ExternalFilterProps, Filter } from './with-external-filter';

type KnownProps = Pick<
  GridOptions,
  | 'treeData'
  | 'excludeChildrenWhenTreeDataFiltering'
  | 'groupDefaultExpanded'
  | 'getDataPath'
>;

type RequiredProps = Pick<GridOptions, 'onGridReady'>;

type GetGroupPath = (record: Record<string, unknown>) => string[];

const getFilteredPath = memoize(
  (paths: string[]) => ({
    true: paths.length === 1 ? paths : [paths[paths.length - 1]],
    false: paths,
  }),
  (paths) => paths.join(','),
);

export const withGrouping =
  (getGroupPath: GetGroupPath) =>
  <T extends KnownProps & ExternalFilterProps & RequiredProps>(
    Component: ComponentType<T>,
  ): FC<Omit<T, keyof KnownProps>> => {
    const Next: FC<Omit<T, keyof KnownProps> & RequiredProps> = ({
      isPaginated,
      filter,
      onGridReady: onReady,
      ...props
    }) => {
      const [api, , onGridReady] = useOnGridReady(onReady);
      const filterRef = useRef<Filter | null>(filter ?? null);
      const getDataPath = useCallback(
        (record: Record<string, unknown>) =>
          getFilteredPath(getGroupPath(record))[
            (filterRef.current != null).toString()
          ],
        [filterRef],
      );

      useEffect(() => {
        filterRef.current = filter ?? null;
        if (!isPaginated) {
          setTimeout(() => {
            api.current?.refreshClientSideRowModel();
          }, 100); // give some time to set the data path. Look at getDataPath
        }
      }, [api, filter, isPaginated]);

      return (
        <Component
          {...(props as any)}
          isPaginated={isPaginated}
          treeData
          groupDisplayType="custom"
          groupDefaultExpanded={0}
          excludeChildrenWhenTreeDataFiltering
          getDataPath={getDataPath}
          filter={filter}
          onGridReady={onGridReady}
        />
      );
    };

    return setDisplayName(wrapDisplayName(Component, 'WithGrouping'))(Next);
  };
